import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { Row, Col, Card, Table, Badge, Button, Spinner } from 'react-bootstrap';
import { LuShoppingCart, LuBox, LuFileText, LuArrowRight, LuPackage } from "react-icons/lu";
import { getMyOrders } from '../../redux/reducers/portalOrderSlice';
import moment from 'moment';
import { formatCurrency } from '../../utils/currency';

const PortalDashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { businessId } = useParams();
    const { customer } = useSelector((state) => state.portalAuth);
    const { orders, loading } = useSelector((state) => state.portalOrder);
    const currency = useSelector((state) => state.portalAuth?.businessData?.jssettings?.currency || 'CAD');

    useEffect(() => {
        dispatch(getMyOrders({ limit: 5 }));
    }, [dispatch]);

    const stats = [
        { title: 'Open Orders', value: orders.filter(o => !['COMPLETED', 'CANCELLED', 'REJECTED', 'DELIVERED'].includes(o.status)).length, icon: LuPackage, color: '#0ea5e9' },
        { title: 'Unpaid Invoices', value: orders.filter(o => o.invoiceId?.status?.toLowerCase() !== 'paid').length, icon: LuFileText, color: '#f59e0b' },
        { title: 'Total Spent', value: formatCurrency(orders.reduce((acc, o) => acc + (o.totals?.total || 0), 0), currency), icon: LuShoppingCart, color: '#10b981' },
    ];

    const getStatusColor = (status) => {
        const colors = {
            'PENDING_PAYMENT': { bg: '#fef3c7', text: '#f59e0b' },
            'PAID': { bg: '#e0f2fe', text: '#0ea5e9' },
            'ACCEPTED': { bg: '#d1fae5', text: '#10b981' },
            'PROCESSING': { bg: '#ede9fe', text: '#6366f1' },
            'SHIPPED': { bg: '#e0f7fa', text: '#28B0C4' },
            'DELIVERED': { bg: '#d1fae5', text: '#10b981' },
            'CANCELLED': { bg: '#f1f5f9', text: '#64748b' },
            'REJECTED': { bg: '#fee2e2', text: '#ef4444' },
            'COMPLETED': { bg: '#d1fae5', text: '#10b981' },
        };
        return colors[status] || { bg: '#f1f5f9', text: '#64748b' };
    };
    return (
        <div>
            <div className="mb-4">
                <h3 className="fw-bold text-dark">Welcome back, {customer?.name}</h3>
                <p className="text-muted">Manage your orders, browse products, and track your shipments.</p>
            </div>

            <Row className="g-4 mb-5">
                {stats.map((stat, idx) => (
                    <Col key={idx} md={4}>
                        <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                            <Card.Body className="p-4 position-relative">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="rounded-4 p-3 d-flex align-items-center justify-content-center" style={{ background: `${stat.color}15`, color: stat.color }}>
                                        <stat.icon size={24} />
                                    </div>
                                    <div>
                                        <div className="text-muted small fw-semibold uppercase">{stat.title}</div>
                                        <div className="fs-3 fw-bold text-dark">{stat.value}</div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row className="g-4">
                <Col lg={8}>
                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Header className="bg-transparent border-0 p-4 d-flex align-items-center justify-content-between">
                            <h5 className="fw-bold mb-0">Recent Orders</h5>
                            <Button as={Link} to={`/customer-side/${businessId}/orders`} variant="link" className="text-decoration-none p-0 d-flex align-items-center gap-1" style={{ color: "#28B0C4" }}>
                                View All <LuArrowRight size={14} />
                            </Button>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {loading ? (
                                <div className="text-center py-5"><Spinner animation="border" style={{ color: "#28B0C4" }} /></div>
                            ) : orders.length > 0 ? (
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="ps-4 py-3 text-muted small fw-bold">ORDER #</th>
                                                <th className="py-3 text-muted small fw-bold">TOTAL</th>
                                                <th className="py-3 text-muted small fw-bold">STATUS</th>
                                                <th className="pe-4 py-3 text-muted small fw-bold">DATE</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orders.map((order) => {
                                                const colors = getStatusColor(order.status);
                                                return (
                                                    <tr key={order._id} className="cursor-pointer" onClick={() => navigate(`/customer-side/${businessId}/orders/${order._id}`)}>
                                                        <td className="ps-4 fw-semibold text-dark">{order.orderNumber}</td>
                                                        <td>{formatCurrency(order.totals?.total, currency)}</td>
                                                        <td className="py-3">
                                                            {(() => {
                                                                const s = order.status?.toUpperCase();
                                                                switch (s) {
                                                                    case "PAID":
                                                                    case "DELIVERED":
                                                                        return <Badge bg="success-subtle" className="text-success rounded-pill px-3 py-1">{s === 'PAID' ? 'Paid' : 'Delivered'}</Badge>;
                                                                    case "PENDING_PAYMENT":
                                                                        return <Badge bg="warning-subtle" className="text-warning rounded-pill px-3 py-1">Pending Payment</Badge>;
                                                                    case "ACCEPTED":
                                                                        return <Badge bg="info-subtle" className="text-info rounded-pill px-3 py-1">Accepted</Badge>;
                                                                    case "PROCESSING":
                                                                        return <Badge bg="primary-subtle" className="text-primary rounded-pill px-3 py-1">Processing</Badge>;
                                                                    case "SHIPPED":
                                                                        return <Badge bg="info-subtle" className="text-info rounded-pill px-3 py-1">Shipped</Badge>;
                                                                    case "CANCELLED":
                                                                        return <Badge bg="secondary-subtle" className="text-secondary rounded-pill px-3 py-1">Cancelled</Badge>;
                                                                    case "REJECTED":
                                                                        return <Badge bg="danger-subtle" className="text-danger rounded-pill px-3 py-1">Rejected</Badge>;
                                                                    default:
                                                                        return <Badge bg="secondary-subtle" className="text-secondary rounded-pill px-3 py-1">{order.status?.replace('_', ' ')}</Badge>;
                                                                }
                                                            })()}
                                                        </td>
                                                        <td className="pe-4 text-muted small">{moment(order.createdAt).format('MMM D, YYYY')}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="text-center py-5 text-muted">No orders found.</div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden" style={{ background: 'linear-gradient(135deg, #28B0C4 0%, #1c8a9a 100%)' }}>
                        <Card.Body className="p-4 d-flex flex-column text-white">
                            <h4 className="fw-bold mb-3">Quick Actions</h4>
                            <div className="d-grid gap-2">
                                <Button as={Link} to={`/customer-side/${businessId}/products`} className="text-start rounded-3 p-3 d-flex align-items-center gap-3 border-0 shadow-sm fw-semibold">
                                    <div className="rounded-3 p-2 d-flex bg-primary-subtle text-primary">
                                        <LuBox size={20} style={{ color: "#28B0C4" }} />
                                    </div>
                                    Browse Products
                                </Button>
                                <Button as={Link} to={`/customer-side/${businessId}/cart`} className="text-start rounded-3 p-3 d-flex align-items-center gap-3 border-0 shadow-sm fw-semibold">
                                    <div className="rounded-3 p-2 d-flex bg-primary-subtle text-primary">
                                        <LuShoppingCart size={20} style={{ color: "#28B0C4" }} />
                                    </div>
                                    View Cart
                                </Button>
                                <Button as={Link} to={`/customer-side/${businessId}/invoices`}  className="text-start rounded-3 p-3 d-flex align-items-center gap-3 border-0 shadow-sm fw-semibold">
                                    <div className="rounded-3 p-2 d-flex bg-primary-subtle text-primary">
                                        <LuFileText size={20} style={{ color: "#28B0C4" }} />
                                    </div>
                                    My Invoices
                                </Button>
                            </div>

                            <div className="mt-auto pt-4 text-center">
                                <img src="/assets/images/portal-illus.svg" alt="" style={{ maxWidth: '150px', opacity: 0.8 }} />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default PortalDashboard;
